import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SectionHero from '../../../components/shared/SectionHero';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkIcon from '@mui/icons-material/Work';

const ImageContainer = ({ src, alt, caption }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      minHeight: '400px',
      position: 'relative',
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        '&:hover img': {
          transform: 'scale(1.15)',
        },
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'transform 0.8s ease-in-out',
        }}
      />
    </Box>
    <Box
      className="caption"
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '12px 16px',
        textAlign: 'center',
        transition: 'background-color 0.3s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
        },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {caption}
      </Typography>
    </Box>
  </Box>
);

const VisionMission = () => {
  const navigate = useNavigate();

  const strategies = [
    'Aerial Photography',
    'LIDAR Mapping',
    'Digital Mapping',
    'GIS Solutions',
    'Land Information',
    'Cadastral Survey',
  ];

  const capabilities = [
    { name: 'Aerial Photography', value: 95 },
    { name: 'Digital Mapping', value: 90 },
    { name: 'GIS Solutions', value: 85 },
  ];

  return (
    <Box>
      <SectionHero 
        title="Vision & Mission" 
        subtitle="Shaping the Future of Geospatial Solutions around the World" 
      />

      {/* Vision & Mission Section */}
      <Box sx={{ py: 4, backgroundColor: '#fff' }}>
        <Container>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Card 
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    backgroundColor: '#FDF7F7',
                    boxShadow: 'none',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 2,
                        overflow: 'hidden',
                        mr: 2,
                        backgroundColor: '#fff',
                        position: 'relative',
                      }}
                    >
                      <motion.img
                        src="/images/star.png"
                        alt="Mission"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="overline" color="primary.main" sx={{ fontWeight: 600 }}>
                        Our Mission
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Excellence
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    To empower World organizations with cutting-edge geospatial solutions that drive sustainable development and innovation.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Card 
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    backgroundColor: '#FDF7F7',
                    boxShadow: 'none',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 2,
                        overflow: 'hidden',
                        mr: 2,
                        backgroundColor: '#fff',
                        position: 'relative',
                      }}
                    >
                      <motion.img
                        src="/images/mission.png"
                        alt="Vision"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="overline" color="primary.main" sx={{ fontWeight: 600 }}>
                        Our Vision
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Leadership
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    To be the World's leading provider of geospatial solutions, recognized globally for excellence and innovation.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Background Section */}
      <Box sx={{ py: 4, backgroundColor: '#fff' }}>
        <Container>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                style={{ width: '100%', height: '100%' }}
              >
                <ImageContainer
                  src="/images/building.png"
                  alt="Geomaps Headquarters"
                  caption="Geomaps Centre - Our Corporate Headquarters in Upper Hill, Nairobi"
                />
              </motion.div>
            </Grid>
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Card
                  sx={{
                    p: 3,
                    backgroundColor: '#FDF7F7',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <Typography variant="h4" fontWeight={600} gutterBottom>
                    Our Background
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}>
                    Geomaps Africa was founded in February 1983 by Hon. Lenny Kivuti, a professional licensed geospatial engineer. The firm provides a wide range of services in geospatial and geoinformation solutions.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}>
                    Our corporate head office is located at GEOMAPS Centre, situated in Upper Hill Area of Nairobi, 2km south of the City Centre in the new and serene corporate business center of the city. We utilize the 8th, 9th and 10th floors of Geomaps Centre, adequate to provide space for technical staff and equipment as may be required for various projects. 
                  </Typography>
                  <Typography variant="h5" fontWeight={600} sx={{ mb: 2, textAlign: "justify" }}>
                    Our Services Include:
                  </Typography>
                  <Grid container spacing={1}>
                    {[
                      'Aerial Photography and LIDAR Mapping',
                      'Drone Mapping',
                      'Digital Topographic Mapping',
                      'Geodetic GNSS/Real Time Kinematic (RTK) Surveys',
                      'Cadastral Surveys',
                      'Digital Cadastral Database Development',
                      'Engineering Surveys',
                      'Digital Photogrammetric Mapping',
                      'Land Information Management Solutions',
                      'Land Use Planning',
                      'Hydrographic Surveys',
                      'GIS-Based Revenue Enhancement Systems',
                    ].map((service, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircleOutlineIcon sx={{ color: 'primary.main', mr: 1, fontSize: '1.2rem' }} />
                          <Typography variant="body2">{service}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Professional Team Section */}
      <Box sx={{ py: 4, backgroundColor: '#fff' }}>
        <Container>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Card
                  sx={{
                    p: 3,
                    height: '100%',
                    backgroundColor: '#FDF7F7',
                    borderRadius: 2,
                    boxShadow: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="h4" fontWeight={600} gutterBottom>
                    Our Professional Team
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3, flex: 1, textAlign: "justify" }}>
                  Our professionals possess the requisite education, experience, skills, integrity and commitment to quality necessary to meet the standards of our firm. Our team includes professional geospatial engineers, licensed land surveyors, registered survey assistants, Geospatial experts, photogrammetry experts, GIS technicians, survey field crew and administrative staff. We have long-standing partnerships with professionals in other geospatial related fields such as Geologists, Registered Valuers, Natural Resource Experts, Environmental Experts, Agricultural Experts, Land Economists, Sociologists, Financial & Economics Experts among others.
                  </Typography>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/about/leadership')}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      alignSelf: 'flex-start',
                    }}
                  >
                    Meet Our Team
                  </Button>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                style={{ width: '100%', height: '100%' }}
              >
                <ImageContainer
                  src="/images/team/team.jpg"
                  alt="Professional Team"
                  caption="Geomaps Team"
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Global Presence Section */}
      <Box sx={{ py: 4, backgroundColor: '#fff' }}>
        <Container>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                style={{ width: '100%', height: '100%' }}
              >
                <ImageContainer
                  src="/images/locations.png"
                  alt="Global Presence"
                  caption="Our Regional Offices Across East, Central and West Africa"
                />
              </motion.div>
            </Grid>
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Card
                  sx={{
                    p: 3,
                    height: '100%',
                    backgroundColor: '#FDF7F7',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <Typography variant="h4" fontWeight={600} gutterBottom>
                    Global Presence
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6, textAlign: "justify" }}>
                    We are a large multinational consultancy in East and Central Africa, delivering geospatial solutions across the globe. We maintain branch offices in Uganda, Tanzania and Senegal, and are open to other credible professional associations and development partnerships around the globe to ensure geospatial technology is taken to the next level.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Careers Section */}
      <Box sx={{ py: 4, backgroundColor: '#fff' }}>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Card
              sx={{
                p: 4,
                borderRadius: 2,
                boxShadow: 'none',
                color: 'white',
                textAlign: 'justify',
                position: 'relative',
                overflow: 'hidden',
                minHeight: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 0,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    zIndex: 1,
                  },
                }}
              >
                <img
                  src="/images/good.jpg"
                  alt="Careers"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              <Box sx={{ position: 'relative', zIndex: 2, py: 4 }}>
                <WorkIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h3" fontWeight={600} gutterBottom>
                  Join Our Team
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                  Be part of the world’s leading geospatial solutions provider. We're always looking for talented professionals who are passionate about geospatial innovation and excellence.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/about/careers')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                  endIcon={<ArrowForwardIcon />}
                >
                  View Career Opportunities
                </Button>
              </Box>
            </Card>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default VisionMission; 